import React, { useEffect, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { translate } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getAllEntities as getAllBanks } from 'app/entities/bank/bank.reducer';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';

interface IRebateRunSummary {
  id: number;
  bankId: number;
  rebateSettingId: number;
  commodityType: string;
  docType: string;
  referenceNo: string;
  status: string;
  generatedAt: string;
  autoSendAt: string;
  recipients: string;
  approvedBy?: string;
  approvedAt?: string;
  sentAt?: string;
  failureReason?: string;
}

interface IRebateRunLetter {
  referenceNo: string;
  beneficiaryName?: string;
  accountNumber?: string;
  amount?: number;
  currency?: string;
  fileName: string;
  pdfBase64: string;
}

interface IRebateRunDetail extends IRebateRunSummary {
  emailSubject: string;
  emailBodyHtml: string;
  letterFileName: string;
  letterPdfBase64: string;
  // One per letter the run sent (ALSHMOKH-66) — length 1 for a run created before this change.
  letters: IRebateRunLetter[];
  reportRows: any[];
}

const STATUS_TABS = ['', 'PENDING', 'APPROVED_SENT', 'AUTO_SENT', 'FAILED'];

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'success' | 'info' | 'error'> = {
  PENDING: 'warning',
  APPROVED_SENT: 'success',
  AUTO_SENT: 'info',
  FAILED: 'error',
};

const formatDate = (value?: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-');

export const RebateRuns = () => {
  const dispatch = useAppDispatch();
  const banks = useAppSelector(state => state.bank.entities);

  const [statusTab, setStatusTab] = useState(0);
  const [runs, setRuns] = useState<IRebateRunSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);

  const [detailRunId, setDetailRunId] = useState<number | null>(null);
  const [detail, setDetail] = useState<IRebateRunDetail | undefined>(undefined);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'email' | 'letter' | 'report'>('email');
  const [selectedLetterIndex, setSelectedLetterIndex] = useState(0);

  useEffect(() => {
    dispatch(getAllBanks({}));
  }, []);

  const bankName = (bankId: number) => banks?.find(b => b.id === bankId)?.name ?? `#${bankId}`;

  const loadRuns = async () => {
    setLoading(true);
    try {
      const status = STATUS_TABS[statusTab];
      const { data } = await axios.get<IRebateRunSummary[]>(`api/rebate-runs${status ? `?status=${status}` : ''}`);
      setRuns(data);
    } catch (error) {
      toast.error(translate('comexApp.rebateRun.home.notFound'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRuns();
  }, [statusTab]);

  const openDetail = async (id: number) => {
    setDetailRunId(id);
    setDetailTab('email');
    setSelectedLetterIndex(0);
    setDetailLoading(true);
    try {
      const { data } = await axios.get<IRebateRunDetail>(`api/rebate-runs/${id}`);
      setDetail(data);
    } catch (error) {
      setDetail(undefined);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailRunId(null);
    setDetail(undefined);
  };

  const approve = async (id: number) => {
    if (!window.confirm(translate('comexApp.rebateRun.approveConfirm'))) {
      return;
    }
    setApprovingId(id);
    try {
      const { data } = await axios.post<IRebateRunSummary>(`api/rebate-runs/${id}/approve`);
      setRuns(prev => prev.map(run => (run.id === id ? data : run)));
      toast.success(translate('comexApp.rebateRun.approveSuccess'));
    } catch (error) {
      toast.error(translate('comexApp.rebateRun.approveError'));
    } finally {
      setApprovingId(null);
    }
  };

  const downloadReport = async (id: number) => {
    try {
      const { data } = await axios.get<Blob>(`api/rebate-runs/${id}/report-excel`, { responseType: 'blob' });
      saveAs(data, `Rebate_Report_${detail?.referenceNo ?? id}.xlsx`);
    } catch (error) {
      toast.error(translate('comexApp.rebateRun.detail.downloadReportError'));
    }
  };

  return (
    <Container sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {translate('comexApp.rebateRun.home.title')}
      </Typography>

      <Tabs value={statusTab} onChange={(_, value) => setStatusTab(value)} sx={{ mb: 2 }}>
        {STATUS_TABS.map(status => (
          <Tab key={status || 'ALL'} label={translate(`comexApp.rebateRun.status.${status || 'ALL'}`)} />
        ))}
      </Tabs>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('comexApp.rebateRun.table.bank')}</TableCell>
                <TableCell>{translate('comexApp.rebateRun.table.docType')}</TableCell>
                <TableCell>{translate('comexApp.rebateRun.table.referenceNo')}</TableCell>
                <TableCell>{translate('comexApp.rebateRun.table.status')}</TableCell>
                <TableCell>{translate('comexApp.rebateRun.table.generatedAt')}</TableCell>
                <TableCell>{translate('comexApp.rebateRun.table.autoSendAt')}</TableCell>
                <TableCell>{translate('comexApp.rebateRun.table.approvedBy')}</TableCell>
                <TableCell>{translate('comexApp.rebateRun.table.sentAt')}</TableCell>
                <TableCell>{translate('comexApp.rebateRun.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {translate('comexApp.rebateRun.home.notFound')}
                  </TableCell>
                </TableRow>
              ) : (
                runs.map(run => (
                  <TableRow key={run.id} hover>
                    <TableCell>{bankName(run.bankId)}</TableCell>
                    <TableCell>{run.docType}</TableCell>
                    <TableCell>{run.referenceNo}</TableCell>
                    <TableCell>
                      <Chip size="small" label={translate(`comexApp.rebateRun.status.${run.status}`)} color={STATUS_COLOR[run.status] ?? 'default'} />
                    </TableCell>
                    <TableCell>{formatDate(run.generatedAt)}</TableCell>
                    <TableCell>{formatDate(run.autoSendAt)}</TableCell>
                    <TableCell>{run.approvedBy ?? '-'}</TableCell>
                    <TableCell>{formatDate(run.sentAt)}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openDetail(run.id)} sx={{ textTransform: 'none', mr: 1 }}>
                        {translate('comexApp.rebateRun.view')}
                      </Button>
                      {run.status === 'PENDING' && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => approve(run.id)}
                          disabled={approvingId === run.id}
                          sx={{ textTransform: 'none' }}
                        >
                          {approvingId === run.id ? <CircularProgress size={16} /> : translate('comexApp.rebateRun.approve')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={detailRunId !== null} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {translate('comexApp.rebateRun.detail.title')}
          <IconButton onClick={closeDetail} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Tabs value={detailTab} onChange={(_, value) => setDetailTab(value)} sx={{ mb: 2 }}>
                <Tab value="email" label={translate('comexApp.rebateRun.detail.tabEmail')} />
                <Tab value="letter" label={translate('comexApp.rebateRun.detail.tabLetter')} />
                <Tab value="report" label={translate('comexApp.rebateRun.detail.tabReport')} />
              </Tabs>

              {detailTab === 'email' && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {detail.emailSubject}
                  </Typography>
                  <iframe
                    title="rebate-run-email"
                    srcDoc={detail.emailBodyHtml}
                    sandbox=""
                    style={{ width: '100%', height: 420, border: 'none' }}
                  />
                </>
              )}

              {detailTab === 'letter' && (
                <>
                  {detail.letters && detail.letters.length > 1 && (
                    <Tabs
                      value={selectedLetterIndex}
                      onChange={(_, value) => setSelectedLetterIndex(value)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ mb: 1, minHeight: 36 }}
                    >
                      {detail.letters.map((letter, index) => (
                        <Tab
                          key={`${letter.referenceNo}-${index}`}
                          label={letter.beneficiaryName || letter.referenceNo}
                          sx={{ minHeight: 36 }}
                        />
                      ))}
                    </Tabs>
                  )}
                  <iframe
                    title="rebate-run-letter"
                    src={`data:application/pdf;base64,${
                      detail.letters && detail.letters.length > 0 ? detail.letters[selectedLetterIndex]?.pdfBase64 : detail.letterPdfBase64
                    }`}
                    style={{ width: '100%', height: 480, border: 'none' }}
                  />
                </>
              )}

              {detailTab === 'report' && (
                <TableContainer>
                  {(detail.reportRows ?? []).length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => downloadReport(detail.id)} sx={{ textTransform: 'none' }}>
                        {translate('comexApp.rebateRun.detail.downloadReport')}
                      </Button>
                    </Box>
                  )}
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Reference</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Currency</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(detail.reportRows ?? []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            -
                          </TableCell>
                        </TableRow>
                      ) : (
                        detail.reportRows.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>{r.referenceNumber}</TableCell>
                            <TableCell>{r.customerName}</TableCell>
                            <TableCell>{r.date ? dayjs(r.date).format('YYYY-MM-DD') : '-'}</TableCell>
                            <TableCell>{r.amount}</TableCell>
                            <TableCell>{r.currency}</TableCell>
                            <TableCell>{r.status}</TableCell>
                            <TableCell>{r.pendingNote}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default RebateRuns;
